import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Repeat } from "lucide-react";
import type { Service } from "@/lib/types";
import { money } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

export function ServiceCard({
  service,
  creatorHandle,
  open = true,
}: {
  service: Service;
  creatorHandle: string;
  open?: boolean;
}) {
  const isInstant = service.quote_mode === "instant";
  return (
    <Link
      to={`/${creatorHandle}/${service.slug}`}
      className="group block focus:outline-none"
    >
      <Card className="h-full transition-all group-hover:-translate-y-0.5 group-hover:shadow-md">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="text-base leading-snug">{service.title}</CardTitle>
            <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
          {service.description && (
            <p className="line-clamp-3 text-sm text-muted-foreground">{service.description}</p>
          )}
        </CardHeader>
        <CardContent className="space-y-2 pb-3">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-semibold tracking-tight">
              {money(service.starting_price_cents, service.currency)}
            </span>
            <span className="text-xs text-muted-foreground">starting</span>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> ~{service.delivery_days}d delivery
            </span>
            <span className="inline-flex items-center gap-1">
              <Repeat className="h-3.5 w-3.5" /> {service.revision_rounds} revisions
            </span>
          </div>
        </CardContent>
        <CardFooter className="justify-between pt-0">
          <Badge variant={isInstant ? "default" : "secondary"} className="font-normal">
            {isInstant ? "Instant quote" : "Quote on request"}
          </Badge>
          <Badge
            variant="outline"
            className={
              open
                ? "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
                : "border-neutral-300 text-muted-foreground"
            }
          >
            {open ? "Open" : "Waitlist"}
          </Badge>
        </CardFooter>
      </Card>
    </Link>
  );
}
